'use client'
import { Typography } from '@repo/ui/components'
import { useTranslate } from '@repo/shared/hooks'
import { useEffect, useState, useRef } from 'react'
import type { IOtpResendProps } from './otp.types'
import { RESEND_TIME } from '@repo/shared/variables/otp.var'

export const OtpResend = (props: IOtpResendProps) => {
  const { onResend, loading = false, error = false } = props
  const { t } = useTranslate()
  const [countdown, setCountdown] = useState(RESEND_TIME)
  const [canResend, setCanResend] = useState(false)
  const prevLoading = useRef(loading)

  useEffect(() => {
    if (prevLoading.current && !loading && !error) {
      setTimeout(() => {
        setCountdown(RESEND_TIME)
        setCanResend(false)
      }, 200)
    }
    prevLoading.current = loading
  }, [loading, error])

  useEffect(() => {
    if (loading) {
      setCanResend(false)
    } else if (error) {
      setCanResend(true)
    }
  }, [loading, error])

  useEffect(() => {
    if (countdown > 0 && !loading && !error) {
      const timer = setTimeout(() => {
        setCountdown(countdown - 1)
      }, 1000)
      return () => clearTimeout(timer)
    } else if (countdown === 0 && !loading && !error) {
      setCanResend(true)
    }
  }, [countdown, loading, error])

  const formatTime = (seconds: number) => {
    const minutes = Math.floor(seconds / 60)
    const remainingSeconds = seconds % 60
    return `${minutes.toString().padStart(2, '0')}:${remainingSeconds
      .toString()
      .padStart(2, '0')}`
  }

  const handleResend = (e: React.MouseEvent<HTMLDivElement>) => {
    e.preventDefault()
    e.stopPropagation()
    if (canResend && !loading) {
      onResend()
    }
  }

  return (
    <Typography
      size='sm'
      weight='bold'
      className={`text-base-surface-100 cursor-pointer ${
        canResend && !loading ? 'hover:text-primary-500' : ''
      }`}
      onClick={handleResend}
    >
      {loading
        ? t('common_sending')
        : canResend
          ? t('common_otp_resend')
          : t('common_otp_countdown', { time: formatTime(countdown) })}
    </Typography>
  )
}
